import { ApiResponse, FormStore, GlobalStore } from '@roc/feature-app-core';
import { action, computed, flow, makeObservable, observable } from 'mobx';
import { DrawFieldFormStore } from './drawFieldFormStore';
import { DrawService } from '../services/drawService';

const initialForm = {
  fields: {
    drawAmount: {
      value: '',
      error: null,
      rule: '',
    },
  },
  meta: {
    key: null,
    editMode: false,
    isValid: false,
    showErrors: null,
    error: null,
  },
}

export class DrawInformationFormStore extends FormStore {
  private globalStore: GlobalStore;
  private drawService: DrawService;
  drawFieldFormStores: DrawFieldFormStore[];

  basicFields: any[];
  basicFieldsOptions: any[];

  constructor(globalStore) {
    super(initialForm, globalStore);

    this.globalStore = globalStore;
    this.drawService = new DrawService();
    makeObservable(this, {
      drawFieldFormStores: observable,
      addNewDrawField: action,
      deleteDrawField: action,
      fetchDrawInformation: flow,
      finalWireAmount: computed,
      updateDrawInformation: flow,
      validateDrawInformation: action
    });
    this.reset();
  }

  reset() {
    super.reset();
    this.drawFieldFormStores = [];
    this.basicFields = [];
    this.basicFieldsOptions = [];
  }

  addNewDrawField() {
    this.drawFieldFormStores = [new DrawFieldFormStore(this.globalStore), ...this.drawFieldFormStores];
  }

  deleteDrawField(uuid: string) {
    this.drawFieldFormStores = this.drawFieldFormStores.filter(store => store.uuid !== uuid);
  }

  getDrawInformation(drawId: number) {
    const drawFields = this.drawFieldFormStores.map(store => store.getFlattenedValues());
    return {
      drawId,
      amount: this.form.fields.drawAmount.value,
      drawFields
    };
  }

  *fetchDrawInformation(drawId: number) {
    try {

      this.reset();
      const basicFieldsResponse: ApiResponse = yield this.drawService.getBasicFields();
      const basicFieldsData = basicFieldsResponse?.data?.data;
      this.basicFields = basicFieldsData.sort();
      this.basicFieldsOptions = [{ label: "Select an option", value: "" }, ...basicFieldsData.map(op => ({ label: op, value: op }))];


      const drawInfoResponse: ApiResponse = yield this.drawService.getDrawById(drawId);
      const drawInfoData = drawInfoResponse?.data?.data;
      const drawFields = JSON.parse(drawInfoData.payableCustomFieldStr) || [];

      this.onFieldChange("drawAmount", drawInfoData.amount);

      drawFields.forEach(df => {
        const newStore = new DrawFieldFormStore(this.globalStore);
        newStore.loadForm({
          ...df,
          amount: df.amount,
        });
        this.drawFieldFormStores.push(newStore);
      });
    } catch (err) {
      console.error(err);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'There was an error getting the draw details.',
      });
    }
  }

  get finalWireAmount() {
    const drawAmount = this.form.fields.drawAmount.value ?? 0;
    if (drawAmount === '0' || drawAmount === 0) {
      return 0;
    }
    const drawFieldsAmount = this.drawFieldFormStores.reduce((accumulator, store) => accumulator + (+store.form.fields.amount?.value || 0), 0);
    return drawAmount - drawFieldsAmount;
  }

  validateDrawInformation() {
    let isValid = true;

    this.drawFieldFormStores.forEach(store => {
      store.runFormValidation();

      isValid = isValid && store.form.meta.isValid;
    });

    return isValid;
  }

  *updateDrawInformation(drawId: number) {
    try {
      if (!this.validateDrawInformation()) {
        this.globalStore.notificationStore.showErrorNotification({
          message: 'Please complete all the required fields.',
        });
        return false;
      }
      const data = this.getDrawInformation(drawId);
      yield this.drawService.updateDrawInformation(data);
      this.globalStore.notificationStore.showSuccessNotification({
        message: "Draw Information updated successfully.",
      });
      return true;
    } catch (err) {
      console.error(err);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'There was an error updating the draw information.',
      });
      return false;
    }
  }
}